import { faArrowLeft, faBars, faCheck, faFileAlt, faFileImage, faFilePdf, faPrint, faSpinner, faTimes, faUpload, faCheckCircle, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import moment from "moment-timezone";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../../css/JuanScope/ScopeRegistration1.css";
import SJDEFILogo from "../../images/SJDEFILogo.png";
import SessionManager from '../JuanScope/SessionManager';
import DocumentVerificationSystem from "./DocumentVerificationSystem";
import SideNavigation from "./SideNavigation";
import WaiverFormModal from "./WaiverFormModal";

function ScopeAdmissionRequirements() {
  const navigate = useNavigate();
  const [userData, setUserData] = useState({});
  const [admissionAdminFirstStatus, setAdmissionAdminFirstStatus] = useState("On-going");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [nextLocation, setNextLocation] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [showWaiverModal, setShowWaiverModal] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    DocumentVerification,
    handleFileUpload: verificationHandleFileUpload,
    handleVerificationComplete,
    getVerificationType,
    files,
    verificationResults,
  } = DocumentVerificationSystem();

  const getOverallStatus = () => {
    if (requirements.length === 0) return { status: "Unverified", isValid: false };

    const allVerified = requirements.every(req => req.waived || req.feedback === "Document verified");

    return {
      status: allVerified ? "Valid" : "Invalid",
      isValid: allVerified,
      feedback: allVerified ? "" : ""
    };
  };

  useEffect(() => {
    const userEmail = localStorage.getItem("userEmail");
    const createdAt = localStorage.getItem("createdAt");

    if (!userEmail || !createdAt) {
      navigate("/scope-login", { state: { error: "No active session found. Please log in." } });
      return;
    }

    const fetchWithRetry = async (url, retries = 3, delay = 1000) => {
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP error ${response.status}`);
        return await response.json();
      } catch (error) {
        if (retries > 0) {
          await new Promise((resolve) => setTimeout(resolve, delay));
          return fetchWithRetry(url, retries - 1, delay);
        }
        throw error;
      }
    };

    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");

        const createdAtDate = new Date(createdAt);
        if (isNaN(createdAtDate.getTime())) {
          handleLogout();
          navigate("/scope-login", { state: { accountInactive: true, error: "Invalid session data. Please log in again." } });
          return;
        }

        const verificationData = await fetchWithRetry(`${process.env.REACT_APP_API_URL}/api/enrollee-applicants/verification-status/${userEmail}`);

        if (verificationData.status !== "Active" || Math.abs(new Date(verificationData.createdAt).getTime() - new Date(createdAt).getTime()) > 1000) {
          handleLogout();
          navigate("/scope-login", { state: { accountInactive: true, error: "Account is inactive or session expired." } });
          return;
        }

        const userDataResponse = await fetchWithRetry(`${process.env.REACT_APP_API_URL}/api/enrollee-applicants/activity/${userEmail}?createdAt=${encodeURIComponent(createdAt)}`);
        const applicantData = await fetchWithRetry(`${process.env.REACT_APP_API_URL}/api/enrollee-applicants/personal-details/${userEmail}`);

        localStorage.setItem("applicantID", applicantData.applicantID || userDataResponse.applicantID || "");
        localStorage.setItem("firstName", applicantData.firstName || userDataResponse.firstName || "");
        localStorage.setItem("middleName", applicantData.middleName || "");
        localStorage.setItem("lastName", applicantData.lastName || userDataResponse.lastName || "");
        localStorage.setItem("dob", applicantData.dob ? new Date(applicantData.dob).toISOString().split("T")[0] : "");
        localStorage.setItem("nationality", applicantData.nationality || "");

        setUserData({
          email: userEmail,
          firstName: applicantData.firstName || userDataResponse.firstName || "User",
          middleName: applicantData.middleName || "",
          lastName: applicantData.lastName || userDataResponse.lastName || "",
          dob: applicantData.dob ? new Date(applicantData.dob).toISOString().split("T")[0] : "",
          nationality: applicantData.nationality || "",
          studentID: applicantData.studentID || userDataResponse.studentID || "N/A",
          applicantID: applicantData.applicantID || userDataResponse.applicantID || "N/A",
          entryLevel: applicantData.entryLevel || "",
          admissionRequirements: applicantData.admissionRequirements || [],
        });

        const entryLevel = applicantData.entryLevel || "";
        const fetchedAdmissionStatus = applicantData.admissionAdminFirstStatus || "On-going";
        setAdmissionAdminFirstStatus(fetchedAdmissionStatus);

        let reqList = [];
        const normalizedEntryLevel = entryLevel?.toLowerCase();

        if (normalizedEntryLevel === "senior high school") {
          reqList = [
            { id: 1, name: "Photocopy of latest report card (photocopy of Gr 10 latest semester or Gr 9 Report Card)", submitted: null, waived: false, feedback: "No document uploaded - Required unless waived", waiverDetails: null },
            { id: 2, name: "ID (2x2) Photo – White background", submitted: null, waived: false, feedback: "No document uploaded - Required unless waived", waiverDetails: null },
          ];
        } else if (normalizedEntryLevel === "senior high school - transferee") {
          reqList = [
            { id: 1, name: "ID (2x2) Photo – White background", submitted: null, waived: false, feedback: "No document uploaded - Required unless waived", waiverDetails: null },
            { id: 2, name: "Transcript of records or certification of grades from previous school – for evaluation purposes", submitted: null, waived: false, feedback: "No document uploaded - Required unless waived", waiverDetails: null },
          ];
        } else if (normalizedEntryLevel === "college - freshman") {
          reqList = [
            { id: 1, name: "Original Senior High School Report Card (F138)", submitted: null, waived: false, feedback: "No document uploaded - Required unless waived", waiverDetails: null },
            { id: 2, name: "ID (2x2) Photo – White background", submitted: null, waived: false, feedback: "No document uploaded - Required unless waived", waiverDetails: null },
            { id: 3, name: "Certificate of Good Moral Character", submitted: null, waived: false, feedback: "No document uploaded - Required unless waived", waiverDetails: null },
          ];
        } else if (normalizedEntryLevel === "college - transferee") {
          reqList = [
            { id: 1, name: "Transcript of Records from previous college", submitted: null, waived: false, feedback: "No document uploaded - Required unless waived", waiverDetails: null },
            { id: 2, name: "ID (2x2) Photo – White background", submitted: null, waived: false, feedback: "No document uploaded - Required unless waived", waiverDetails: null },
            { id: 3, name: "Certificate of Good Moral Character", submitted: null, waived: false, feedback: "No document uploaded - Required unless waived", waiverDetails: null },
          ];
        } else {
          reqList = [
            { id: 1, name: "ID (2x2) Photo – White background", submitted: null, waived: false, feedback: "No document uploaded - Required unless waived", waiverDetails: null },
          ];
          setError(`Entry level '${entryLevel || "Not specified"}' is not fully configured. Some features may be limited. Please contact support if you experience issues.`);
        }

        let admissionData;
        try {
          admissionData = await fetchWithRetry(`${process.env.REACT_APP_API_URL}/api/enrollee-applicants/admission-requirements/${userEmail}`);
        } catch (err) {
          admissionData = null;
        }

        let examInterviewData;
        try {
          examInterviewData = await fetchWithRetry(`${process.env.REACT_APP_API_URL}/api/enrollee-applicants/exam-interview/${userEmail}`);
        } catch (err) {
          // Handle silently
        }

        let examDetailsData;
        try {
          examDetailsData = await fetchWithRetry(`${process.env.REACT_APP_API_URL}/api/enrollee-applicants/exam-details/${userEmail}`);
        } catch (err) {
          // Handle silently
        }

        if (admissionData && Array.isArray(admissionData.admissionRequirements) && admissionData.admissionRequirements.length > 0) {
          setRequirements(admissionData.admissionRequirements.map((req) => ({
            id: req.requirementId,
            name: req.name,
            submitted: req.fileName || null,
            waived: req.status === "Waived",
            feedback: req.status === "Waived" ? "Waiver approved" : req.status === "Verified" ? "Document verified" : req.status === "Submitted" ? "Document uploaded" : req.verificationMessage ? `Invalid document - ${req.verificationMessage}` : "No document uploaded",
            waiverDetails: req.waiverDetails,
            fileUrl: req.imageUrl,
            fileType: req.fileType,
            storageProvider: req.storageProvider,
            foreignKey: req.foreignKey,
          })));
          setAdmissionAdminFirstStatus(admissionData.admissionAdminFirstStatus || fetchedAdmissionStatus);
          setIsSubmitted(admissionData.admissionRequirementsStatus === "Complete");
        } else {
          setRequirements(reqList);
          if (!admissionData) {
            setError("Failed to load admission requirements. Using default requirements.");
          }
        }

        if (applicantData.registrationStatus !== "Complete") {
          navigate("/scope-registration-6");
          return;
        }

        if (examInterviewData?.preferredExamAndInterviewApplicationStatus !== "Complete") {
          navigate("/scope-exam-interview-application");
          return;
        }

        setLoading(false);
      } catch (err) {
        setError("Failed to load user data or admission requirements. Please check your connection and try again.");
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  useEffect(() => {
    const checkAccountStatus = async () => {
      try {
        const userEmail = localStorage.getItem("userEmail");
        const createdAt = localStorage.getItem("createdAt");

        if (!userEmail || !createdAt) {
          navigate("/scope-login", { state: { error: "Session expired. Please log in." } });
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/enrollee-applicants/verification-status/${userEmail}`);
        if (!response.ok) throw new Error(`HTTP error ${response.status}`);
        const data = await response.json();

        if (data.status !== "Active" || new Date(data.createdAt).getTime() !== new Date(createdAt).getTime()) {
          handleLogout();
          navigate("/scope-login", { state: { accountInactive: true, error: "Account is inactive or session expired." } });
        }
      } catch (err) {
        setError("Unable to verify account status. Please check your connection.");
      }
    };

    const interval = setInterval(checkAccountStatus, 60 * 1000);
    checkAccountStatus();
    return () => clearInterval(interval);
  }, [navigate]);

  useEffect(() => {
    if (admissionAdminFirstStatus === "Approved" || admissionAdminFirstStatus === "Rejected") {
      if (admissionAdminFirstStatus === "Rejected") {
        setError("Your admission requirements have been rejected. Please check the Admission Exam Details for more information or contact the admissions office.");
      }
    }
  }, [admissionAdminFirstStatus]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isFormDirty && !isSubmitted) {
        e.preventDefault();
        e.returnValue = "You have unsaved changes. Are you sure you want to leave?";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isFormDirty, isSubmitted]);

  useEffect(() => {
    Object.keys(verificationResults).forEach((id) => {
      const result = verificationResults[id];
      if (result.status !== "pending") {
        setRequirements((prev) =>
          prev.map((req) =>
            req.id === parseInt(id)
              ? {
                  ...req,
                  feedback: result.status === "verified" ? "Document verified" : `Invalid document - ${result.message || "Verification failed"}`,
                }
              : req
          )
        );
      }
    });
  }, [verificationResults]);

   const sendSystemLog = async (payload) => {
    if (!payload.action) return;
    try {
      const logResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/system-logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (logResponse.ok) {
        const logResData = await logResponse.json(); // Optional
        console.log('System log recorded:', logResData.message || logResData); // Optional
      } else {
        const errData = await logResponse.json().catch(() => ({ message: 'Failed to parse log error response' })); // Optional
        console.error('Failed to record system log - Server:', logResponse.status, errData.message || errData); // Optional
      }
    } catch (logErr) {
      console.error('Failed to record system log - Network/Fetch:', logErr); // Optional
    }
  };

  const handleLogout = async () => {
    const applicantIDForLog = localStorage.getItem('applicantID') || 'N/A';
    const firstNameForLog = localStorage.getItem('firstName') || 'User';
    const lastNameForLog = localStorage.getItem('lastName') || '';
    const userEmailForLog = localStorage.getItem('userEmail') || 'Unknown Email'; 

    const accountNameForLog = `${firstNameForLog} ${lastNameForLog}`.trim() || userEmailForLog;

    const logData = {
      userID: applicantIDForLog, 
      accountName: accountNameForLog,
      role: 'Enrollee Applicant', 
      action: 'Logged Out',
      detail: `User ${accountNameForLog} (${applicantIDForLog}) logged out from Scope.`,
    };
    await sendSystemLog(logData);

    try {
      const userEmail = localStorage.getItem("userEmail");
      const createdAt = localStorage.getItem("createdAt");

      if (!userEmail) {
        navigate("/scope-login", { state: { error: "No active session found." } });
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/enrollee-applicants/logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: userEmail, createdAt }),
      });

      if (response.ok) {
        localStorage.clear();
        navigate("/scope-login");
      } else {
        setError("Failed to log out. Please try again.");
      }
    } catch (err) {
      setError("Error during logout process. Please check your connection.");
    } finally {
      setShowLogoutModal(false);
    }
  };

  const prepareData = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const base64String = reader.result.split(",")[1];
          resolve({
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            lastModified: file.lastModified,
            rawData: base64String,
          });
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileUpload = async (id, event) => {
    if (isSubmitted) {
      alert("Submission is complete. You cannot upload or modify files.");
      return;
    }

    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ["image/png", "image/jpeg", "application/pdf"];
    if (!validTypes.includes(file.type)) {
      alert("Only PNG, JPG, or PDF files are allowed.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert("File size must not exceed 10MB.");
      return;
    }

    try {
      const fileData = await prepareData(file);
      setUploadedFiles((prev) => ({ ...prev, [id]: file }));
      verificationHandleFileUpload(id, event);

      setRequirements((prev) =>
        prev.map((req) =>
          req.id === id
            ? {
                ...req,
                submitted: file.name,
                feedback: "Document uploaded, verification in progress...",
                waiverDetails: null,
                waived: false,
                fileData,
              }
            : req
        )
      );
      setIsFormDirty(true);
    } catch (error) {
      alert("Failed to process file. Please try again.");
    }
  };

  const handleViewDocument = async (id) => {
    try {
      const requirement = requirements.find((req) => req.id === id);
      if (!requirement) {
        alert("No file available to view.");
        return;
      }

      if (requirement.fileUrl) {
        window.open(requirement.fileUrl, "_blank");
        return;
      }

      const uploadedReq = userData?.admissionRequirements?.find((r) => r.id === requirement.id);
      if (uploadedReq?.imageUrl) {
        window.open(uploadedReq.imageUrl, "_blank");
        return;
      }

      const file = uploadedFiles[id];
      if (file) {
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL, "_blank");
        URL.revokeObjectURL(fileURL);
      } else {
        alert("No file available to view.");
      }
    } catch (err) {
      alert("Failed to view document. Please try again.");
    }
  };

  const handleWaiveCredentials = () => {
    if (isSubmitted) {
      alert("Submission is complete. You cannot waive credentials.");
      return;
    }
    setShowWaiverModal(true);
  };

  const handleWaiverSubmit = (waiverData) => {
    if (isSubmitted) {
      alert("Submission is complete. You cannot modify waivers.");
      return;
    }

    setRequirements((prev) =>
      prev.map((req) =>
        waiverData.selectedRequirements.includes(req.id)
          ? {
              ...req,
              waived: true,
              submitted: null,
              feedback: "Waiver requested",
              waiverDetails: { reason: waiverData.reason, promiseDate: waiverData.promiseDate },
            }
          : req.waived && !waiverData.selectedRequirements.includes(req.id)
          ? {
              ...req,
              waived: false,
              feedback: "No document uploaded - Required unless waived",
              waiverDetails: null,
            }
          : req
      )
    );
    setIsFormDirty(true);
    setShowWaiverModal(false);
  };

  const handleSave = async () => {
    try {
      const userEmail = localStorage.getItem("userEmail");
      if (!userEmail) {
        setError("User email not found. Please log in again.");
        navigate("/scope-login");
        return false;
      }

      if (!requirements || requirements.length === 0) {
        setError("No requirements found. Please refresh the page and try again.");
        return false;
      }

      setIsSubmitting(true);
      const optimizedFormData = new FormData();
      optimizedFormData.append("email", userEmail);

      const requirementsForAPI = requirements.map((req) => ({
        id: req.id,
        name: req.name,
        waived: req.waived,
        waiverDetails: req.waiverDetails,
        submitted: req.submitted,
        fileMetadata: req.fileData ? { fileName: req.fileData.fileName, fileType: req.fileData.fileType, fileSize: req.fileData.fileSize } : null,
      }));

      optimizedFormData.append("requirementsData", JSON.stringify(requirementsForAPI));

      Object.keys(uploadedFiles).forEach((id) => {
        if (uploadedFiles[id] instanceof File) {
          optimizedFormData.append(`file-${id}`, uploadedFiles[id]);
        }
      });

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/enrollee-applicants/save-admission-requirements`, optimizedFormData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setIsFormDirty(false);
      setRequirements(
        response.data.admissionRequirements.map((req) => ({
          id: req.requirementId,
          name: req.name,
          submitted: req.fileName || null,
          waived: req.status === "Waived",
          feedback: req.status === "Waived" ? "Waiver approved" : req.status === "Verified" ? "Document verified" : req.status === "Submitted" ? "Document uploaded" : req.verificationMessage ? `Invalid document - ${req.verificationMessage}` : "No document uploaded",
          waiverDetails: req.waiverDetails,
          fileUrl: req.imageUrl,
          fileType: req.fileType,
          storageProvider: req.storageProvider,
          foreignKey: req.foreignKey,
        }))
      );
      setAdmissionAdminFirstStatus(response.data.admissionAdminFirstStatus || "On-going");
      setIsSubmitted(response.data.admissionRequirementsStatus === "Complete");
      setIsSubmitting(false);
      return true;
    } catch (err) {
      let errorMessage = "Failed to save admission requirements. Please try again.";
      if (err.response) {
        errorMessage = err.response.data?.error || `Server error: ${err.response.status}`;
      } else if (err.request) {
        errorMessage = "No response received from server. Please check your connection.";
      }
      setError(errorMessage);
      setIsSubmitting(false);
      return false;
    }
  };

  const handlePrintWaiver = async () => {
    const waivedRequirements = requirements.filter((req) => req.waived && req.waiverDetails);
    if (waivedRequirements.length === 0) {
      alert("No waived requirements to print.");
      return;
    }

    try {
      const userEmail = localStorage.getItem("userEmail");
      if (!userEmail) {
        setError("User email not found. Please log in again.");
        navigate("/scope-login");
        return;
      }

      const now = moment().tz("Asia/Manila");
      const dateIssued = now.format("YYYY-MM-DD");
      const dateSigned = now.format("YYYY-MM-DDTHH:mm:ssZ");

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/generate-waiver-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userData: { firstName: userData.firstName, lastName: userData.lastName, email: userEmail },
          waivedRequirements,
          academicYear: "2025-2026",
          dateIssued,
          dateSigned,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to generate waiver PDF");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      URL.revokeObjectURL(url);
    } catch (err) {
      setError("Failed to generate waiver PDF. Please check your connection and try again.");
    }
  };

  const handleNext = async () => {
    if (!requirements || requirements.length === 0) {
      alert("No requirements found. Please refresh the page or contact support.");
      return;
    }

    const { isValid } = getOverallStatus();
    if (!isValid && !(admissionAdminFirstStatus === "Approved" || admissionAdminFirstStatus === "Rejected")) {
      alert("All requirements must be either waived or verified to proceed. Please address any invalid or unverified requirements.");
      return;
    }

    if (isSubmitted || admissionAdminFirstStatus === "Approved" || admissionAdminFirstStatus === "Rejected") {
      navigate("/scope-admission-exam-details");
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const userEmail = localStorage.getItem("userEmail");
      if (!userEmail) {
        setError("User email not found. Please log in again.");
        navigate("/scope-login");
        setIsSubmitting(false);
        return;
      }

      const saved = await handleSave();
      if (!saved) {
        setError("Failed to save requirements. Please try again.");
        setIsSubmitting(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/enrollee-applicants/complete-admission-requirements`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: userEmail }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to complete admission requirements");
      }

      setIsSubmitted(true);
      setAdmissionAdminFirstStatus(data.admissionAdminFirstStatus || "On-going");
      setShowConfirmModal(false);
      alert("Admission requirements submitted successfully. Your submission is being validated. Please check back for updates.");
      navigate("/scope-admission-exam-details");
    } catch (err) {
      setError(`Submission failed: ${err.message}. Please ensure all requirements are uploaded or waived, then try again or contact support.`);
      setShowConfirmModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelSubmit = () => {
    setShowConfirmModal(false);
  };

  const handleBack = () => {
    if (isFormDirty && !isSubmitted) {
      setNextLocation("/scope-exam-interview-application");
      setShowUnsavedModal(true);
    } else {
      navigate("/scope-exam-interview-application");
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const handleModalConfirm = () => {
    setShowUnsavedModal(false);
    if (nextLocation) navigate(nextLocation);
  };

  const handleModalCancel = () => {
    setShowUnsavedModal(false);
    setNextLocation(null);
  };

  const handleNavigation = (path) => {
    if (isFormDirty && !isSubmitted) {
      setNextLocation(path);
      setShowUnsavedModal(true);
    } else {
      navigate(path);
    }
  };

  return (
    <SessionManager>
      <div className="scope-registration-container">
        <header className="juan-register-header">
          <div className="juan-header-left">
            <img src={SJDEFILogo} alt="SJDEFI Logo" className="juan-logo-register" />
            <div className="juan-header-text">
              <h1>JUAN SCOPE</h1>
            </div>
          </div>
          <div className="hamburger-menu">
            <button className="hamburger-button" onClick={toggleSidebar} aria-label="Toggle navigation menu">
              <FontAwesomeIcon icon={sidebarOpen ? faTimes : faBars} size="lg" />
            </button>
          </div>
        </header>
        <div className="scope-registration-content">
          <SideNavigation userData={userData} onNavigate={closeSidebar} isOpen={sidebarOpen} />
          <main className={`scope-main-content ${sidebarOpen ? "sidebar-open" : ""}`}>
            {loading ? (
              <div className="scope-loading">Loading...</div>
            ) : error ? (
              <div className="scope-error">{error}</div>
            ) : (
              <div className="registration-content">
                <h2 className="registration-title">Admission Requirements</h2>
                <div className="registration-divider"></div>
                <div className="registration-container">
                  <div style={{ fontSize: "12px", marginBottom: "1.5rem", color: "#333" }}>
                    <strong>Entry Level:</strong> {userData.entryLevel || "Not specified"}
                  </div>

                  {!isSubmitted && (
                    <div className="overall-status-container" style={{ margin: "1rem 0", padding: "1rem", backgroundColor: "#f8f9fa", borderRadius: "8px", border: "1px solid #e0e0e0" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <strong>Overall Status:</strong>
                        {getOverallStatus().isValid ? (
                          <>
                            <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#34A853" }} />
                            <span style={{ color: "#34A853" }}>Valid</span>
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faTimesCircle} style={{ color: "#EA4335" }} />
                            <span style={{ color: "#EA4335" }}>Invalid</span>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {isSubmitted && !(admissionAdminFirstStatus === "Approved" || admissionAdminFirstStatus === "Rejected") && (
                    <div style={{ margin: "1rem 0", color: "#333", fontSize: "14px", backgroundColor: "#e0f7fa", padding: "1rem", borderRadius: "5px" }}>
                      <p>Your submission is complete and under validation. Please check the Admission Exam Details page for further updates.</p>
                    </div>
                  )}
                  {(admissionAdminFirstStatus === "Approved" || admissionAdminFirstStatus === "Rejected") && (
                    <div style={{ margin: "1rem 0", color: "#333", fontSize: "14px", backgroundColor: admissionAdminFirstStatus === "Approved" ? "#e0f7fa" : "#ffebee", padding: "1rem", borderRadius: "5px" }}>
                      <p>
                        Your admission requirements have been {admissionAdminFirstStatus.toLowerCase()}. The details are now available on the{" "}
                        <Link to="/scope-admission-exam-details" style={{ color: "#2A67D5", textDecoration: "underline" }} onClick={(e) => { e.preventDefault(); handleNavigation("/scope-admission-exam-details"); }}>
                          Admission Exam Details
                        </Link>{" "}
                        page.
                      </p>
                    </div>
                  )}
                  <div className="personal-info-section">
                    <div className="personal-info-header">
                      <FontAwesomeIcon icon={faFileAlt} style={{ color: "#212121" }} />
                      <h3>{isSubmitted ? "View Requirements" : "Upload Requirements"}</h3>
                    </div>
                    <div className="personal-info-divider"></div>
                    {!isSubmitted && (
                      <div className="reminder-box">
                        <p>
                          <strong>Reminders:</strong> When uploading requirements:
                          <ul style={{ paddingLeft: "20px", margin: "5px 0 0 0", listStyleType: "disc" }}>
                            <li>Verification system will check your uploaded documents for authenticity, completeness, and accuracy.</li>
                            <li>All documents must be marked as verified by Feedback to continue admission application.</li>
                            <li>It only accepts an image (PNG or JPG) and PDF file.</li>
                            <li>Size of each uploaded file must not exceed 10MB.</li>
                            <li>You can only upload one (1) file per requirement.</li>
                          </ul>
                        </p>
                      </div>
                    )}
                    <div style={{ overflowX: "auto", marginTop: "1rem" }}>
                      <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: "0", border: "1px solid #2A67D5", backgroundColor: "#CFE0FF", borderRadius: "8px", overflow: "hidden" }}>
                        <thead>
                          <tr style={{ backgroundColor: "#2A67D5", color: "white" }}>
                            {!isSubmitted && (
                              <th style={{ padding: "12px", textAlign: "left", fontSize: "13px", fontWeight: "bold", borderBottom: "1px solid #A2A2A2" }}>
                                Actions
                              </th>
                            )}
                            <th style={{ padding: "12px", textAlign: "left", fontSize: "13px", fontWeight: "bold", borderBottom: "1px solid #A2A2A2" }}>
                              Requirement
                            </th>
                            <th style={{ padding: "12px", textAlign: "left", fontSize: "13px", fontWeight: "bold", borderBottom: "1px solid #A2A2A2" }}>
                              Submitted Copy
                            </th>
                            <th style={{ padding: "12px", textAlign: "left", fontSize: "13px", fontWeight: "bold", borderBottom: "1px solid #A2A2A2" }}>
                              Waived
                            </th>
                            <th style={{ padding: "12px", textAlign: "left", fontSize: "13px", fontWeight: "bold", borderBottom: "1px solid #A2A2A2" }}>
                              Feedback
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {requirements.map((req) => {
                            const uploadedReq = userData?.admissionRequirements?.find((r) => r.id === req.id);
                            const isVerified = req.feedback === "Document verified";
                            const isInvalid = req.feedback.includes("Invalid");

                            return (
                              <tr key={req.id} style={{ backgroundColor: "white", transition: "background-color 0.2s", border: isInvalid ? "2px solid red" : "none" }} onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f8f9fa")} onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "white")}>
                                {!isSubmitted && (
                                  <td style={{ padding: "12px", verticalAlign: "top" }}>
                                    <div style={{ display: "flex", gap: "8px" }}>
                                      <label style={{ cursor: "pointer", backgroundColor: "#00245A", color: "white", padding: "6px 12px", borderRadius: "6px", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "5px" }}>
                                        <FontAwesomeIcon icon={faUpload} />
                                        Upload
                                        <input type="file" accept=".png,.jpg,.jpeg,.pdf" style={{ display: "none" }} onChange={(e) => handleFileUpload(req.id, e)} disabled={req.waived || isSubmitted} />
                                      </label>
                                    </div>
                                  </td>
                                )}
                                <td style={{ padding: "12px", fontSize: "12px", verticalAlign: "top" }}>
                                  <div style={{ display: "flex", gap: "8px" }}>
                                    {req.name}
                                    {isVerified && <FontAwesomeIcon icon={faCheck} style={{ color: "#34A853" }} />}
                                    {isInvalid && <FontAwesomeIcon icon={faTimes} style={{ color: "#EA4335" }} />}
                                  </div>
                                </td>
                                <td style={{ padding: "12px", fontSize: "12px", verticalAlign: "top" }}>
                                  {req.submitted && !req.waived ? (
                                    <button style={{ background: "none", border: "none", cursor: "pointer", color: "#2A67D5", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "5px" }} onClick={() => handleViewDocument(req.id)}>
                                      <FontAwesomeIcon icon={req.fileType?.includes("pdf") ? faFilePdf : faFileImage} />
                                      {req.submitted}
                                    </button>
                                  ) : uploadedReq ? (
                                    <button style={{ background: "none", border: "none", cursor: "pointer", color: "#2A67D5", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "5px" }} onClick={() => handleViewDocument(req.id)}>
                                      <FontAwesomeIcon icon={uploadedReq.fileType?.includes("pdf") ? faFilePdf : faFileImage} />
                                      {uploadedReq.fileName}
                                    </button>
                                  ) : (
                                    "-"
                                  )}
                                </td>
                                <td style={{ padding: "12px", fontSize: "12px", verticalAlign: "top" }}>
                                  {req.waived ? <FontAwesomeIcon icon={faCheck} style={{ color: "#34A853" }} /> : "-"}
                                </td>
                                <td style={{ padding: "12px", fontSize: "12px", verticalAlign: "top", whiteSpace: "pre-line" }}>
                                  {req.feedback}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ display: "none" }}>
                      {requirements.map((req) => (
                        <DocumentVerification key={req.id} file={files[req.id]} requirementType={getVerificationType(req.name)} onVerificationComplete={(result) => handleVerificationComplete(req.id, result)} />
                      ))}
                    </div>
                    {!isSubmitted && (
                      <div style={{ display: "flex", gap: "10px", marginTop: "1rem", flexWrap: "wrap" }}>
                        <button style={{ backgroundColor: "#00245A", color: "white", padding: "8px 16px", borderRadius: "8px", fontSize: "12px", border: "none", cursor: isSubmitted ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: "5px" }} onClick={handleWaiveCredentials} disabled={isSubmitted}>
                          <FontAwesomeIcon icon={faFileAlt} />
                          Waive Credentials
                        </button>
                        <button style={{ backgroundColor: "#4285F4", color: "white", padding: "8px 16px", borderRadius: "8px", fontSize: "12px", border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "5px" }} onClick={handlePrintWaiver}>
                          <FontAwesomeIcon icon={faPrint} />
                          Print Waiver
                        </button>
                      </div>
                    )}
                    <div className="form-buttons">
                      <button type="button" className="back-button" onClick={handleBack}>
                        <FontAwesomeIcon icon={faArrowLeft} />
                        Back
                      </button>
                      <button
                        type="button"
                        className="next-button"
                        onClick={handleNext}
                        disabled={!(getOverallStatus().isValid || admissionAdminFirstStatus === "Approved" || admissionAdminFirstStatus === "Rejected")}
                        style={{ backgroundColor: getOverallStatus().isValid || admissionAdminFirstStatus === "Approved" || admissionAdminFirstStatus === "Rejected" ? "#34A853" : "#d3d3d3", cursor: getOverallStatus().isValid || admissionAdminFirstStatus === "Approved" || admissionAdminFirstStatus === "Rejected" ? "pointer" : "not-allowed" }}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </main>
        </div>
        {sidebarOpen && <div className="sidebar-overlay active" onClick={toggleSidebar}></div>}
        {showLogoutModal && (
          <div className="scope-modal-overlay">
            <div className="scope-confirm-modal">
              <h3>Confirm Logout</h3>
              <p>Are you sure you want to logout?</p>
              <div className="scope-modal-buttons">
                <button className="scope-modal-cancel" onClick={() => setShowLogoutModal(false)}>
                  Cancel
                </button>
                <button className="scope-modal-confirm" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}
        {showUnsavedModal && (
          <div className="scope-modal-overlay">
            <div className="scope-confirm-modal">
              <h3>Unsaved Changes</h3>
              <p>You have unsaved changes. Do you want to leave without saving?</p>
              <div className="scope-modal-buttons">
                <button className="scope-modal-cancel" onClick={handleModalCancel}>
                  Stay
                </button>
                <button className="scope-modal-confirm" onClick={handleModalConfirm}>
                  Leave
                </button>
              </div>
            </div>
          </div>
        )}
        {showConfirmModal && (
          <div className="scope-modal-overlay">
            <div className="scope-confirm-modal">
              <h3>Confirm Submission</h3>
              <p>You are about to submit your admission requirements. Once submitted, you cannot make changes. Are you sure you want to proceed?</p>
              <div className="scope-modal-buttons">
                <button className="scope-modal-cancel" onClick={handleCancelSubmit} disabled={isSubmitting}>
                  Cancel
                </button>
                <button className={`scope-modal-confirm ${isSubmitting ? "scope-modal-button-loading" : ""}`} onClick={handleConfirmSubmit} disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin className="scope-modal-spinner" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    "Submit"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        <WaiverFormModal isOpen={showWaiverModal} onClose={() => setShowWaiverModal(false)} onSubmit={handleWaiverSubmit} requirements={requirements} userData={userData} />
      </div>
    </SessionManager>
  );
}

export default ScopeAdmissionRequirements;